// React
import { useState, useRef, useCallback, useEffect } from "react";

export function useColumnResize({ Columns, SelectedColumns, OnColumnResize, InitialColumnWidths }) {
    const [columnWidths, setColumnWidths] = useState(InitialColumnWidths || {});
    const columnWidthsRef = useRef(InitialColumnWidths || {});
    const tableRef = useRef(null);
    const dragState = useRef({
        active: false,
        fieldName: null,
        startX: 0,
        startWidth: 0,
        minWidth: 50,
        maxWidth: Infinity,
    });

    // Keep the latest OnColumnResize without changing handler identities (so document
    // listeners can be removed with the exact reference they were added with).
    const onColumnResizeRef = useRef(OnColumnResize);
    onColumnResizeRef.current = OnColumnResize;

    const getVisibleColumns = useCallback(() => {
        if (!Columns) return [];
        if (SelectedColumns && SelectedColumns.length > 0) {
            return Columns.filter(c => c.Visible !== false && (c.Locked || SelectedColumns.includes(c.FieldName)));
        }
        return Columns.filter(c => c.Visible !== false);
    }, [Columns, SelectedColumns]);

    const getColumnWidth = useCallback((fieldName) => {
        return columnWidths[fieldName] || null;
    }, [columnWidths]);

    const handleMouseMove = useCallback((event) => {
        if (!dragState.current.active) return;

        const delta = event.clientX - dragState.current.startX;
        let newWidth = dragState.current.startWidth + delta;

        newWidth = Math.max(newWidth, dragState.current.minWidth);
        newWidth = Math.min(newWidth, dragState.current.maxWidth);

        const fieldName = dragState.current.fieldName;
        const updated = { ...columnWidthsRef.current, [fieldName]: newWidth };
        columnWidthsRef.current = updated;
        setColumnWidths(updated);
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!dragState.current.active) return;

        const fieldName = dragState.current.fieldName;
        dragState.current.active = false;

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        if (typeof onColumnResizeRef.current === "function") {
            onColumnResizeRef.current({ FieldName: fieldName, Width: columnWidthsRef.current[fieldName] });
        }
    }, [handleMouseMove]);

    const startResize = useCallback((fieldName, minWidth, maxWidth, event) => {
        event.preventDefault();
        event.stopPropagation();

        const th = event.target.closest("th");
        const currentWidth = th ? th.getBoundingClientRect().width : (columnWidths[fieldName] || 200);

        dragState.current = {
            active: true,
            fieldName,
            startX: event.clientX,
            startWidth: currentWidth,
            minWidth: minWidth || 50,
            maxWidth: maxWidth || Infinity,
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, [columnWidths, handleMouseMove, handleMouseUp]);

    const autoFitColumn = useCallback((fieldName) => {
        if (!tableRef.current) return;

        const visibleColumns = getVisibleColumns();
        const columnIndex = visibleColumns.findIndex(c => c.FieldName === fieldName);
        if (columnIndex === -1) return;

        const table = tableRef.current;
        const rows = table.querySelectorAll("tr");
        let maxContentWidth = 0;

        rows.forEach(row => {
            const cells = row.querySelectorAll("th, td");
            // Find the cell for this column - account for spacer/reorder cell at index 0
            let dataIndex = 0;
            let targetCell = null;
            for (let i = 0; i < cells.length; i++) {
                const cell = cells[i];
                if (cell.classList.contains("eone-ui-table-row-spacer") ||
                    cell.classList.contains("eone-ui-table-row-drag-button") ||
                    cell.classList.contains("eone-ui-table-row-drag-button-dragging") ||
                    cell.classList.contains("eone-ui-table-header-spacer")) {
                    continue;
                }
                if (cell.classList.contains("eone-ui-table-row-button-group") ||
                    cell.classList.contains("eone-ui-row-button-table-header")) {
                    continue;
                }
                if (dataIndex === columnIndex) {
                    targetCell = cell;
                    break;
                }
                dataIndex++;
            }

            if (targetCell) {
                const origWidth = targetCell.style.width;
                const origMaxWidth = targetCell.style.maxWidth;
                const origOverflow = targetCell.style.overflow;
                targetCell.style.width = "auto";
                targetCell.style.maxWidth = "none";
                targetCell.style.overflow = "visible";
                maxContentWidth = Math.max(maxContentWidth, targetCell.scrollWidth);
                targetCell.style.width = origWidth;
                targetCell.style.maxWidth = origMaxWidth;
                targetCell.style.overflow = origOverflow;
            }
        });

        if (maxContentWidth === 0) return;

        const col = visibleColumns[columnIndex];
        const minWidth = col?.MinWidth || 50;
        const maxWidth = col?.MaxWidth || Infinity;
        const finalWidth = Math.max(minWidth, Math.min(maxContentWidth + 16, maxWidth));

        const updated = { ...columnWidthsRef.current, [fieldName]: finalWidth };
        columnWidthsRef.current = updated;
        setColumnWidths(updated);

        if (typeof OnColumnResize === "function") {
            OnColumnResize({ FieldName: fieldName, Width: finalWidth });
        }
    }, [Columns, getVisibleColumns, OnColumnResize]);

    const resetWidths = useCallback((widths) => {
        const newWidths = widths || {};
        columnWidthsRef.current = newWidths;
        setColumnWidths(newWidths);
    }, []);

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [handleMouseMove, handleMouseUp]);

    return {
        tableRef,
        columnWidths,
        getColumnWidth,
        startResize,
        autoFitColumn,
        resetWidths,
    };
}
